import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Globe,
  Search,
  Loader2,
  ArrowRight,
  AlertCircle,
  X,
} from 'lucide-react';
import { useOrganization } from '../../hooks/useOrganization';
import { useFetchOrganizationLogo } from '../../hooks/useFetchOrganizationLogo';
import { CompanyCandidatesList, FetchedLogoPreviewCard } from '@/shared/ui';

export interface FetchOrganizationLogoModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess?: (logoUrl: string) => void;
}

export const FetchOrganizationLogoModal: React.FC<FetchOrganizationLogoModalProps> = ({
  open,
  onOpenChange,
  onSuccess,
}) => {
  const { currentOrganization } = useOrganization();
  const {
    fetchLogo,
    selectCandidate,
    reset,
    applyLogo,
    isFetching,
    isApplying,
    candidates,
    fetchedResult,
    fetchError,
  } = useFetchOrganizationLogo();

  const [query, setQuery] = useState('');

  // Pre-fill query with organization name when modal opens
  useEffect(() => {
    if (open) {
      setQuery(currentOrganization?.name || '');
      reset();
    }
  }, [open, currentOrganization?.name, reset]);

  const handleSearch = async (e?: React.FormEvent) => {
    if (e) e.preventDefault();
    if (!query.trim() || isFetching) return;
    await fetchLogo(query.trim());
  };

  const handleSelectCandidate = async (domain: string) => {
    if (isFetching) return;
    await selectCandidate(domain);
  };

  const handleApply = async () => {
    if (!fetchedResult?.logoUrl || isApplying) return;
    try {
      const savedUrl = await applyLogo(fetchedResult.logoUrl);
      onSuccess?.(savedUrl);
      onOpenChange(false);
    } catch {
      // Error toast is already dispatched by mutation
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px] p-0 overflow-hidden bg-white border border-gray-200 rounded-2xl shadow-xl [&>button:last-child]:hidden focus:outline-none">
        {/* Header */}
        <div className="p-6 pb-5 border-b border-gray-100 bg-gradient-to-b from-purple-50/50 to-white relative">
          <div className="flex items-start gap-4 pr-10">
            <div className="w-11 h-11 rounded-xl bg-[#F5EEFE] flex items-center justify-center shrink-0 border border-purple-100">
              <Globe className="w-5 h-5 text-[#9333EA]" />
            </div>
            <div>
              <DialogTitle className="text-lg font-bold text-gray-900 tracking-tight">
                Fetch Organization Logo
              </DialogTitle>
              <p className="text-xs text-gray-500 mt-1 leading-relaxed">
                Enter your company name or website URL. We&apos;ll automatically find and extract your official logo.
              </p>
            </div>
          </div>

          <button
            type="button"
            onClick={() => onOpenChange(false)}
            className="absolute right-5 top-5 p-1.5 rounded-lg text-slate-400 hover:text-slate-600 transition-colors focus:outline-none border-none outline-none"
            aria-label="Close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content Body */}
        <div className="p-6 space-y-5">
          {/* Search Input Form */}
          <form onSubmit={handleSearch} className="space-y-2">
            <Label htmlFor="search-input" className="text-xs font-semibold text-slate-700">
              Company Name or Website URL
            </Label>
            <div className="flex gap-2.5">
              <div className="relative flex-1">
                <Search className="w-4 h-4 absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400" />
                <Input
                  id="search-input"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="e.g. Nike or nike.com"
                  className="pl-10 h-11 rounded-xl border-slate-200 text-sm placeholder:text-slate-400 focus-visible:ring-0 focus-visible:ring-offset-0 focus-visible:border-[#A655F8] focus:border-[#A655F8] focus:outline-none transition-colors"
                  disabled={isFetching || isApplying}
                />
              </div>
              <Button
                type="submit"
                disabled={!query.trim() || isFetching || isApplying}
                className="h-11 px-5 rounded-xl bg-[#A655F8] text-white hover:bg-[#A655F8] active:bg-[#A655F8] font-medium text-sm flex items-center justify-center gap-2 shrink-0 transition-none shadow-none"
              >
                {isFetching ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    <span>Searching...</span>
                  </>
                ) : (
                  <>
                    <span>Fetch Logo</span>
                    <ArrowRight className="w-4 h-4" />
                  </>
                )}
              </Button>
            </div>
          </form>

          {/* Search Candidates List */}
          {!fetchedResult && (
            <CompanyCandidatesList
              candidates={candidates}
              onSelectCandidate={handleSelectCandidate}
              isLoading={isFetching}
            />
          )}

          {/* Fetched Logo Found Card */}
          {fetchedResult && (
            <FetchedLogoPreviewCard
              result={fetchedResult}
              onApply={handleApply}
              onReset={() => {
                reset();
                setQuery('');
              }}
              isApplying={isApplying}
              applyLabel="Apply as Organization Logo"
              resetLabel="Search Again"
            />
          )}

          {/* Fetch Error Display */}
          {fetchError && !fetchedResult && (
            <div className="rounded-xl border border-red-200 bg-red-50 p-3.5 flex items-start gap-2.5">
              <AlertCircle className="w-4 h-4 text-red-600 shrink-0 mt-0.5" />
              <div className="text-xs text-red-700">
                <p className="font-semibold">Unable to fetch logo</p>
                <p className="mt-0.5 text-red-600">{fetchError}</p>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-4 px-6 bg-slate-50/40 border-t border-slate-100 flex justify-end">
          <Button
            type="button"
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={isApplying}
            className="h-10 px-5 rounded-xl border-slate-200 text-slate-700 bg-white hover:bg-white hover:text-slate-700 shadow-none font-medium text-sm transition-none"
          >
            Cancel
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
